import React, { useState, useEffect } from 'react';
import { ScrollView, Text, View, TouchableOpacity, Linking, Image, StyleSheet, Platform, Dimensions, Alert } from 'react-native';
import { Toast } from 'native-base';
import Dialog, { DialogTitle, DialogContent, DialogButton, DialogFooter } from 'react-native-popup-dialog';
import { useDispatch } from 'react-redux'; // Reduxのdispatchをインポート
import { signOut } from '../store/modules/accounts';
import { useNavigate } from 'react-router-dom'; // Web用のナビゲーション
import { MdChevronLeft } from 'react-icons/md';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, app } from '../plugins/firebase'; // Firebase認証
import { getFirestore, doc, getDoc, collection, updateDoc, query, where, getDocs } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';

const firestore = getFirestore();
const functions = getFunctions(app);

const Settings = ({ navigation }) => {
  const isWeb = Platform.OS === 'web'; // Webかどうかを判定
  const webNavigate = isWeb ? useNavigate() : null; // Web用のナビゲーション
  const dispatch = useDispatch(); // dispatch関数を取得

  const [isPremium, setIsPremium] = useState(false);
  const [trialDaysLeft, setTrialDaysLeft] = useState(null);
  const [trialUsed, setTrialUsed] = useState(true);
  const [isTrial, setIsTrial] = useState(false);
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userRef = doc(firestore, 'accounts', user.uid);
        const userSnap = await getDoc(userRef);
  
        if (userSnap.exists()) {
          const userData = userSnap.data();
          setIsPremium(userData.premiumFlag || false);
  
          const now = new Date();
          let daysLeft = null;
          let trial = false;
  
          if (userData.contractRenewDate) {
            const contractDate = new Date(userData.contractRenewDate);
            if (contractDate > now) {
              daysLeft = Math.ceil((contractDate - now) / (1000 * 60 * 60 * 24));
              trial = true;
            }
          }
  
          const premiumRef = collection(firestore, `accounts/${user.uid}/premium`);
          const premiumSnap = await getDocs(query(premiumRef));
          if (!premiumSnap.empty) {
            const latest = premiumSnap.docs.sort((a, b) => {
              return b.data().createdAt?.toMillis() - a.data().createdAt?.toMillis();
            })[0];
            const cancelAt = latest.data().cancelAt;
            if (cancelAt) {
              const cancelAtDate = new Date(cancelAt);
              if (cancelAtDate > now) {
                daysLeft = Math.ceil((cancelAtDate - now) / (1000 * 60 * 60 * 24));
                trial = false;
              }
            }
          }
  
          setTrialDaysLeft(daysLeft);
          setIsTrial(trial);
        }
      }
    });
  
    return () => unsubscribe();
  }, []);

  //プレミアム解約確認
  const handleCancelPremium = () => {
    if (isWeb) {
      if (window.confirm("プレミアム機能を解約してよろしいですか？")) {
        performCancelPremium();
      }
    } else {
      Alert.alert(
        "プレミアム機能解約",
        "プレミアム機能を解約してよろしいですか？",
        [
          { text: "キャンセル", style: "cancel" },
          { text: "OK", onPress: performCancelPremium }
        ]
      );
    }
  };

  // 本番環境用のエンドポイント
  const CANCEL_SUBSCRIPTION_ENDPOINT_PROD = 'https://us-central1-trinity-kurose.cloudfunctions.net/cancelStripeSubscription';
  // テスト環境用のエンドポイント
  const CANCEL_SUBSCRIPTION_ENDPOINT_TEST = 'https://us-central1-trinity-app-test.cloudfunctions.net/cancelStripeSubscription';

  const cancelSubscriptionEndpoint = CANCEL_SUBSCRIPTION_ENDPOINT_PROD;
  //const cancelSubscriptionEndpoint = CANCEL_SUBSCRIPTION_ENDPOINT_TEST;

  // プレミアム解約処理
  const performCancelPremium = async () => {
    const user = auth.currentUser;
    if (!user) {
      console.warn("ログインしていません");
      return;
    }
  
    try {
      // 最新のプレミアム情報を確認（フロント側のチェックはそのまま残す）
      const premiumCollectionRef = collection(firestore, `accounts/${user.uid}/premium`);
      const premiumSnapshot = await getDocs(query(premiumCollectionRef));
  
      if (premiumSnapshot.empty) {
        alert("解約対象のプレミアム情報が見つかりませんでした。");
        return;
      }
  
      const latest = premiumSnapshot.docs.sort((a, b) => {
        return b.data().createdAt?.toMillis() - a.data().createdAt?.toMillis();
      })[0];
      const data = latest.data();
  
      if (data.autoPayment === false) {
        const cancelAt = data.cancelAt ? new Date(data.cancelAt) : null;
        const contractRenewDate = data.contractRenewDate ? new Date(data.contractRenewDate) : null;
        const now = new Date();
  
        const formattedDate =
          cancelAt && cancelAt > now
            ? `${cancelAt.getFullYear()}/${(cancelAt.getMonth() + 1).toString().padStart(2, '0')}/${cancelAt.getDate().toString().padStart(2, '0')}`
            : contractRenewDate && contractRenewDate > now
            ? `${contractRenewDate.getFullYear()}/${(contractRenewDate.getMonth() + 1).toString().padStart(2, '0')}/${contractRenewDate.getDate().toString().padStart(2, '0')}`
            : null;
  
        const message = `プレミアム機能はすでに解約済みです。${formattedDate ? `\n${formattedDate} まではプレミアム機能をご利用いただけます。` : ''}`;
        Platform.OS === 'web' ? alert(message) : Alert.alert("プレミアム機能解約", message);
        return;
      }
  
      // ✅ Cloud Functions onRequest エンドポイントを fetch POST で呼び出す
      const response = await fetch(cancelSubscriptionEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ uid: user.uid })
      });
  
      const result = await response.json();
  
      if (result.success) {
        Platform.OS === 'web'
          ? alert("プレミアム機能を解約しました。")
          : Alert.alert("プレミアム機能解約", "プレミアム機能を解約しました。");
      } else {
        const message = result.error || '解約に失敗しました。';
        console.error("解約失敗:", message);
        Platform.OS === 'web'
          ? alert("解約エラー: " + message)
          : Alert.alert("エラー", message);
      }
  
    } catch (error) {
      console.error("通信エラー:", error);
      Platform.OS === 'web'
        ? alert("通信エラーが発生しました")
        : Alert.alert("エラー", "通信エラーが発生しました");
    }
  };

  const [screenWidth, setScreenWidth] = useState(Dimensions.get('window').width);
  const [isMobileScreen, setIsMobileScreen] = useState(screenWidth < 768);
  useEffect(() => {
    // 画面サイズ変更を監視
    const onChange = ({ window }) => {
      setScreenWidth(window.width);
      setIsMobileScreen(window.width < 768);
    };

    const subscription = Dimensions.addEventListener('change', onChange);

    return () => {
      // クリーンアップ
      subscription?.remove();
    };
  }, []);

  const [isShowDialog, setIsShowDialog] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogMessage, setDialogMessage] = useState('');
  const [dialogEvent, setDialogEvent] = useState({
    onSuccess: null,
    onCancel: null,
  });

  const kurose_url = "http://www.kurose-pf.co.jp/";
  const avis_url = "http://avis-kurose-pf.shop-pro.jp/";
  const mail_url = "http://www.kurose-pf.co.jp/lovenoteentry";

  const showToast = (message) => {
    if (isWeb) {
      alert(message); // Web用にアラートを使用
    } else {
      Toast.show({
        position: 'top',
        type: 'warning',
        text: message,
        buttonText: "OK",
        duration: 3000,
      });
    }
  };

  const handleSignOut = () => {
    if (isWeb) {
      if (window.confirm("ログアウトしてよろしいですか？")) {
        performSignOut();
      }
    } else {
      setDialogTitle("ログアウト");
      setDialogMessage("ログアウトしてよろしいですか");
      setDialogEvent({
        onSuccess: performSignOut,
        onCancel: () => setIsShowDialog(false),
      });
      setIsShowDialog(true);
    }
  };

  const performSignOut = () => {
    setIsShowDialog(false);
    dispatch(signOut()) // Reduxの非同期アクションを呼び出す
      .unwrap() // 成功またはエラーをキャッチするためにunwrapを使用
      .then(() => {
        showToast("ログアウトしました");
        if (isWeb) {
          webNavigate('/login'); // Webの場合はuseNavigateで/loginに遷移
        } else {
          navigation.reset({
            index: 0,
            routes: [{ name: 'Login' }], // モバイルの場合はReact Navigationで遷移
          });
        }
      })
      .catch((error) => {
        showToast(`ログアウトに失敗しました: ${error}`);
      });
  };

  const handleOpenURL = (url) => {
    if (isWeb) {
      window.open(url, '_blank');
    } else {
      Linking.openURL(url).catch(() => showToast("URLを開けませんでした。"));
    }
  };

  const handleNavigation = (route) => {
    if (isWeb) {
      webNavigate(`/${route.toLowerCase()}`); // Web用ナビゲーションで絶対パスを使用
    } else {
      navigation.navigate(route); // モバイル用ナビゲーション
    }
  };

  return (
      <ScrollView>
          <TouchableOpacity style={styles.listItemBack} onPress={() => handleNavigation('Home')}>
            <MdChevronLeft size={20} color="#cd2829" />
            <Text style={styles.controlBtnBackText}>戻る</Text>
          </TouchableOpacity>

        <View style={styles.container}>
          <TouchableOpacity style={styles.listItem} onPress={() => handleNavigation('Mypage')}>
            <Text style={styles.listItemHeader}>マイページ</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.listItem} onPress={() => handleNavigation('Terms')}>
            <Text style={styles.listItemHeader}>利用規約</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.listItem} onPress={() => handleNavigation('Privacy')}>
            <Text style={styles.listItemHeader}>プライバシーポリシー</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.listItem} onPress={() => handleNavigation('Disclaimer')}>
            <Text style={styles.listItemHeader}>免責事項</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.listItem} onPress={() => handleOpenURL(mail_url)}>
            <Text style={styles.listItemHeader}>お問い合わせ</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.listItem} onPress={() => handleNavigation('Law')}>
            <Text style={styles.listItemHeader}>特商法取引に基づく表記</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.listItem} onPress={handleSignOut}>
            <Text style={styles.listItemHeader}>ログアウト</Text>
          </TouchableOpacity>
          {isPremium && (
            <TouchableOpacity style={styles.listItem} onPress={handleCancelPremium}>
              <Text style={styles.listItemHeader}>
                プレミアム解約
                {trialDaysLeft !== null && (
                  <Text style={{ color: '#555555' }}>
                    （{isTrial ? `無料体験期間：あと${trialDaysLeft}日` : `プレミアム会員期間：あと${trialDaysLeft}日`}）
                  </Text>
                )}
              </Text>
            </TouchableOpacity>
          )}
          <View style={styles.subTitle}>
            <Text style={styles.subTitleText}>運営会社</Text>
          </View>

          <TouchableOpacity style={styles.listItemBig} onPress={() => handleOpenURL(kurose_url)}>
            <View style={styles.itemLeft}>
              <Image source={require('../assets/images/kurose_icon.png')} style={styles.image} />
            </View>
            <View style={styles.itemRight}>
            <Text style={styles.listItemHeader}>
              {screenWidth < 768
                ? "株式会社 黒瀬ペットフード\nオフィシャルサイト"
                : "株式会社 黒瀬ペットフードオフィシャルサイト"}
            </Text>
            </View>
          </TouchableOpacity>

          <TouchableOpacity style={styles.listItemBig} onPress={() => handleOpenURL(avis_url)}>
            <View style={styles.itemLeft}>
              <Image source={require('../assets/images/avis_icon.png')} style={styles.image} />
            </View>
            <View style={styles.itemRight}>
              <Text style={styles.listItemHeader}>
                {screenWidth < 768
                  ? "公式オンラインショップ\nアウィス小鳥のフード・飼育用品通販サイト"
                  : "公式オンラインショップ アウィス小鳥のフード・飼育用品通販サイト"}
              </Text>
            </View>
          </TouchableOpacity>
        </View>
        {!isWeb && (
        <Dialog
          visible={isShowDialog}
          onTouchOutside={() => setIsShowDialog(false)}
          width={0.8}
        >
          <DialogTitle title={dialogTitle || '確認'} align="center" />
          <DialogContent style={{ justifyContent: 'center', padding: 10 }}>
            <Text style={{ textAlign: 'left' }}>{dialogMessage}</Text>
          </DialogContent>
          <DialogFooter>
            <DialogButton text="キャンセル" onPress={dialogEvent.onCancel || (() => setIsShowDialog(false))} />
            <DialogButton text="OK" bordered onPress={dialogEvent.onSuccess || (() => setIsShowDialog(false))} />
          </DialogFooter>
        </Dialog>
      )}
      </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    width: '100%',
    maxWidth: 800,
    marginHorizontal: 'auto',
  },
  listItem: {
    flexDirection: 'row',
    paddingTop: 15,
    paddingBottom: 15,
    paddingLeft: 20,
    width: '100%',
    borderBottomColor: '#ddd',
    borderBottomWidth: 1,
  },
  listItemHeader: {
    color: '#555555',
    fontSize: 15,
    width: '100%',
  },
  subTitle: {
    flexDirection: 'row',
    paddingTop: 30,
    paddingBottom: 14,
    paddingLeft: 20,
    width: '100%',
    height: 70,
    borderBottomColor: '#ddd',
    borderBottomWidth: 1,
  },
  subTitleText: {
    color: '#444444',
    fontSize: 16,
    fontWeight: 'bold',
  },
  listItemBig: {
    flexDirection: 'row',
    paddingTop: 15,
    paddingBottom: 15,
    paddingLeft: 20,
    width: '100%',
    borderBottomColor: '#ddd',
    borderBottomWidth: 1,
    alignItems: 'center',
  },
  itemLeft: {
    width: '15%',
  },
  itemRight: {
    width: '85%',
  },
  image: {
    marginLeft: 5,
    width: 30,
    height: 30,
    resizeMode: 'contain',
  },
  listItemBack: {
    flexDirection: 'row',
    paddingTop: 15,
    paddingBottom: 15,
    paddingLeft: 20,
    width: '100%',
    alignItems: 'center',
  },
  controlBtnBackText: {
    color: '#cd2829',
    fontSize: 18,
    marginLeft: 10,
  },
});

export default Settings;
